import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { clsx } from 'clsx';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  showCloseButton?: boolean;
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  showCloseButton = true,
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const sizes = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
  };

  return createPortal(
    <div
      data-modal-backdrop
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50 animate-fade-in"
      onClick={onClose}
    >
      <div
        className={clsx(
          'bg-app-surface dark:bg-gray-800 rounded-t-2xl sm:rounded-xl shadow-xl w-full',
          sizes[size],
          'animate-slide-up',
          'flex flex-col max-h-[90vh] sm:max-h-[85vh]'
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {(title || showCloseButton) && (
          <div className="relative flex items-center justify-between shrink-0 px-4 py-3 sm:p-6 border-b border-app-border dark:border-gray-700">
            {title && (
              <h2 className="text-lg sm:text-xl font-semibold text-app-text dark:text-white truncate pr-10">{title}</h2>
            )}
            {showCloseButton && (
              <button
                onClick={onClose}
                className="absolute right-3 top-1/2 -translate-y-1/2 sm:static sm:translate-y-0 p-1.5 sm:p-1 text-app-text-muted hover:text-app-text dark:hover:text-gray-300 transition-colors rounded-lg hover:bg-app-surface-muted dark:hover:bg-gray-700"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        )}
        <div
          data-modal-body
          className="flex flex-col min-h-0 overflow-y-auto overflow-x-hidden p-4 sm:p-6"
        >
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
};

